import { AdviseSupplierContract } from '../../../../types/advise';

// upstack_api refuses anything past these (CurrentSupplierSchemas::SUPPLIER's name and LINE_ITEMS' description,
// InvoiceFile::FILE_SIZE_LIMIT, StoredNumbers::INTEGER_LIMIT and its decimal(12,2) and decimal(14,4) amount columns),
// so the screen stops there first.
export const CURRENT_SUPPLIER_NAME_LIMIT = 200;
export const INVOICE_FILE_LIMIT_MB = 10;
export const CONTRACT_TERM_LIMIT = 2147483647;
export const AMOUNT_LIMIT = 10_000_000_000;
export const LINE_DESCRIPTION_LIMIT = 1000;
export const LINE_ITEMS_LIMIT = 500;

const WHOLE_NUMBER = /^\d+$/;
const DOLLARS_AND_CENTS = /^(\d+(\.\d{1,2})?|\.\d{1,2})$/;
const FOUR_PLACES = /^(\d+(\.\d{1,4})?|\.\d{1,4})$/;

const TERM_ERROR = 'Enter a whole number of months.';
const FEE_ERROR = 'Enter an amount of $0 or more, in dollars and cents.';
const UNREADABLE: Partial<Record<keyof AdviseSupplierContract, string>> = {
  end_date: 'Enter a full date.',
  term_months: TERM_ERROR,
  early_termination_fee: FEE_ERROR
};

const within = (format: RegExp, typed: string) => format.test(typed) && Number(typed) < AMOUNT_LIMIT;

/**
 * Why upstack_api would refuse a typed contract field, or null when it would take it (a cleared one saves as unset).
 * A browser reports text it can't read in a number or date field as empty, so `unreadable` tells that from a clear.
 */
export const contractFieldError = (
  field: keyof AdviseSupplierContract,
  text: string,
  unreadable = false
): string | null => {
  if (unreadable) return UNREADABLE[field] ?? null;
  const typed = text.trim();
  if (typed === '') return null;
  if (field === 'term_months') {
    return WHOLE_NUMBER.test(typed) && Number(typed) <= CONTRACT_TERM_LIMIT ? null : TERM_ERROR;
  }
  if (field === 'early_termination_fee') return within(DOLLARS_AND_CENTS, typed) ? null : FEE_ERROR;
  return null;
};

export type LineField = 'description' | 'quantity' | 'unit_cost' | 'monthly_cost';

const LINE_AMOUNTS: Record<Exclude<LineField, 'description'>, { format: RegExp; error: string }> = {
  quantity: { format: FOUR_PLACES, error: 'Enter a quantity of 0 or more, to at most 4 decimal places.' },
  unit_cost: { format: FOUR_PLACES, error: 'Enter a unit cost of $0 or more, to at most 4 decimal places.' },
  monthly_cost: { format: DOLLARS_AND_CENTS, error: FEE_ERROR }
};

/** Why upstack_api would refuse a typed line item field, or null when it would take it (a cleared one saves as unset). */
export const lineFieldError = (field: LineField, text: string): string | null => {
  if (field === 'description') {
    return text.length > LINE_DESCRIPTION_LIMIT ? 'Keep the description to 1,000 characters.' : null;
  }
  const typed = text.trim();
  if (typed === '') return null;
  const { format, error } = LINE_AMOUNTS[field];
  return within(format, typed) ? null : error;
};

/** Why upstack_api would refuse a typed monthly amount, or null when it would take it or nothing is typed. */
export const monthlySpendError = (text: string): string | null => {
  const typed = text.trim();
  return typed === '' || within(DOLLARS_AND_CENTS, typed) ? null : FEE_ERROR;
};

export const monthlyTotalError = (text: string): string | null =>
  text.trim() === '' ? 'Enter a monthly total.' : monthlySpendError(text);
